import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

const latToCyr = {
  'a': 'а', 'b': 'б', 'v': 'в', 'g': 'г', 'd': 'д',
  'đ': 'ђ', 'e': 'е', 'ž': 'ж', 'z': 'з', 'i': 'и',
  'j': 'ј', 'k': 'к', 'l': 'л', 'lj': 'љ', 'm': 'м',
  'n': 'н', 'nj': 'њ', 'o': 'о', 'p': 'п', 'r': 'р',
  's': 'с', 't': 'т', 'ć': 'ћ', 'u': 'у', 'f': 'ф',
  'h': 'х', 'c': 'ц', 'č': 'ч', 'dž': 'џ', 'š': 'ш',
  'A': 'А', 'B': 'Б', 'V': 'В', 'G': 'Г', 'D': 'Д',
  'Đ': 'Ђ', 'E': 'Е', 'Ž': 'Ж', 'Z': 'З', 'I': 'И',
  'J': 'Ј', 'K': 'К', 'L': 'Л', 'Lj': 'Љ', 'LJ': 'Љ', 'M': 'М',
  'N': 'Н', 'Nj': 'Њ', 'NJ': 'Њ', 'O': 'О', 'P': 'П', 'R': 'Р',
  'S': 'С', 'T': 'Т', 'Ć': 'Ћ', 'U': 'У', 'F': 'Ф',
  'H': 'Х', 'C': 'Ц', 'Č': 'Ч', 'Dž': 'Џ', 'DŽ': 'Џ', 'Š': 'Ш'
};

const shouldSkipTransliteration = (text) => {
  if (!text) return false;
  
  const skipPatterns = [
    /https?:\/\//i,           // http:// ili https://
    /www\./i,                 // www.
    /@/,                      // email adrese
    /\.rs\b/i,                // .rs domen
    /\.com\b/i,               // .com domen
    /mailto:/i,               // mailto linkovi
    /wetlands?\.rs/i,         // wetlands.rs ili wetland.rs
    /serbiawetlands\.rs/i,    // serbiawetlands.rs
    /\.bg\.ac\.rs/i,          // institucijski email
    /Wetland Guardian/i,      // Wetland Guardian
    /Latinica/i,               // Latinica
    /Ћирилица/i,               // Ћирилица
  ];
  
  return skipPatterns.some(pattern => pattern.test(text));
};

const transliterate = (text) => {
  if (!text) return text;
  
  if (shouldSkipTransliteration(text)) {
    return text;
  }
  
  let result = '';
  let i = 0;
  
  while (i < text.length) {
    if (i < text.length - 1) {
      const twoChar = text.substring(i, i + 2);
      if (latToCyr[twoChar]) {
        result += latToCyr[twoChar];
        i += 2;
        continue;
      }
    }
    
    const oneChar = text[i];
    result += latToCyr[oneChar] || oneChar;
    i++;
  }
  
  return result;
};

const transliterateDOM = (element) => {
  if (!element) return;
  
  const skipTags = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IMG', 'SVG', 'PATH', 'A'];
  
  if (skipTags.includes(element.tagName)) {
    return;
  }

  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    const parentTag = node.parentElement?.tagName;
    
    if (!skipTags.includes(parentTag)) {
      textNodes.push(node);
    }
  }

  textNodes.forEach(textNode => {
    const originalText = textNode.textContent;
    
    if (shouldSkipTransliteration(originalText)) {
      return;
    }
    
    const transliteratedText = transliterate(originalText);
    if (originalText !== transliteratedText) {
      textNode.textContent = transliteratedText;
    }
  });

  const inputs = element.querySelectorAll('input[placeholder], textarea[placeholder]');
  inputs.forEach(input => {
    if (input.placeholder && !shouldSkipTransliteration(input.placeholder)) {
      input.placeholder = transliterate(input.placeholder);
    }
  });
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [script, setScript] = useState(() => {
    const saved = localStorage.getItem('selectedScript');
    return saved || 'lat';
  });
  
  const [needsUpdate, setNeedsUpdate] = useState(false);

  const t = (key) => {
    const latinText = translations['lat'][key] || key;
    
    if (script === 'cir') {
      return transliterate(latinText);
    }
    
    return latinText;
  };

  const toggleLanguage = () => {
    setScript(prev => {
      const newScript = prev === 'lat' ? 'cir' : 'lat';
      localStorage.setItem('selectedScript', newScript);
      setNeedsUpdate(true);
      return newScript;
    });
  };

  useEffect(() => {
    if (script === 'cir') {
      setTimeout(() => {
        const mainContent = document.querySelector('.App');
        if (mainContent) {
          transliterateDOM(mainContent);
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (needsUpdate) {
      setTimeout(() => {
        if (script === 'cir') {
          const mainContent = document.querySelector('.App');
          if (mainContent) {
            transliterateDOM(mainContent);
          }
        } else {
          window.location.reload();
        }
        setNeedsUpdate(false);
      }, 100);
    }
  }, [needsUpdate, script]);

  useEffect(() => {
    if (script === 'cir') {
      const observer = new MutationObserver(() => {
        const mainContent = document.querySelector('.App');
        if (mainContent) {
          transliterateDOM(mainContent);
        }
      });

      const appMain = document.querySelector('.app-main');
      if (appMain) {
        observer.observe(appMain, {
          childList: true,
          subtree: true,
        });
      }

      return () => observer.disconnect();
    }
  }, [script]);

  return (
    <LanguageContext.Provider value={{ 
      language: script, 
      setLanguage: setScript, 
      t, 
      toggleLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};